import { Fragment } from "react";
import { Dialog, Transition } from '@headlessui/react'
import { useForm } from "react-hook-form";
import { useCreateInvite } from "../../hooks/mutations/useCreateInvite";
import { UserPlus } from "react-feather";

interface ModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

const InviteModal = ({ isOpen, closeModal }: ModalProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();
  const { mutate: mutateCreateInvite, isLoading } = useCreateInvite();

  const onSubmit = (data: any) => {
    mutateCreateInvite(data);
    reset();
    closeModal();
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto bg-gray-900 bg-opacity-90"
          onClose={closeModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full sm:max-w-sm sm:align-middle p-6 my-8 transition-all transform bg-white shadow-xl rounded-md">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <UserPlus className="h-6 w-6 text-green-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    Invite a collaborator
                  </Dialog.Title>
                  <div className="mt-2 text-sm space-y-2">
                    <p className="font-light text-gray-500">Collaborators can view, edit, and create new templates for your organization. They can also create new API keys.</p>
                  </div>
                </div>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="font-light mt-4"
                >
                  <div className="flex flex-col">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email address
                      </label>
                      <input
                        type="text"
                        autoComplete="email"
                        className="mt-1 focus:ring-red-200 focus:border-red-200 block w-full sm:text-sm border-gray-300 rounded-md"
                        {...register("email", {
                          required: {
                            value: true,
                            message: "Email is required.",
                          },
                          pattern: {
                            value:
                              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                            message: "Entered value does not match email format",
                          },
                        })}
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="mt-4 inline-flex justify-center items-center space-x-1 w-full rounded-md text-sm px-4 py-2 bg-red-500 font-medium text-white hover:bg-red-400 focus:outline-none"
                      >
                        Send Invitation
                      </button>
                    </div>
                  </div>
                  <div>
                    {errors.email && (
                      <span className="block mt-2 text-xs text-red-500">
                        {errors.email.message}
                      </span>
                    )}
                  </div>
                </form>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default InviteModal;